import { useState, useEffect } from 'react';
import { Package, ShoppingBag, TrendingUp, Store, CircleDot, Circle, CheckCircle2, Zap } from 'lucide-react';
import { api } from '../../../api/client';

/**
 * Aviso 110 V al comprador (spec 2026-09-22 §5.3).
 * Las 3 claves viven en `app_config_v2`; las lee y escribe /api/rules/voltage-110-message.
 * El cliente de API NO lanza excepción en 4xx: hay que mirar `res.success`.
 */
interface VoltageNoticeConfig {
  enabled: boolean;
  es: string;
  pt: string;
}

function VoltageNoticeCard({ t }: { t: (k: string) => string }) {
  const [cfg, setCfg] = useState<VoltageNoticeConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = () => {
    setLoading(true);
    api.get<VoltageNoticeConfig>('/rules/voltage-110-message').then(res => {
      setLoading(false);
      if (res.success && res.data) {
        setCfg(res.data);
        setMsg(null);
      } else {
        setMsg({ kind: 'error', text: res.error || t('settings.voltageNoticeLoadError') });
      }
    });
  };

  useEffect(load, []);

  const save = async (next: VoltageNoticeConfig) => {
    setSaving(true);
    setMsg(null);
    const res = await api.put<VoltageNoticeConfig>('/rules/voltage-110-message', next);
    setSaving(false);
    if (res.success && res.data) {
      setCfg(res.data);
      setMsg({ kind: 'ok', text: t('settings.voltageNoticeSaved') });
      setTimeout(() => setMsg(null), 2500);
    } else {
      setMsg({ kind: 'error', text: res.error || t('settings.voltageNoticeSaveError') });
    }
  };

  // La carga falló (permiso, servidor caído): se muestra el motivo en vez de
  // dejar la tarjeta invisible, con un botón para volver a intentar.
  if (!cfg) {
    if (loading) return null;
    return (
      <div className="rounded-lg border p-5 border-amber-500/30 bg-amber-500/5">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-amber-500">
          <Zap className="w-4 h-4" />
          {t('settings.voltageNoticeTitle')}
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-red-500">
            {msg?.text || t('settings.voltageNoticeLoadError')}
          </span>
          <button
            onClick={load}
            className="px-2.5 py-1 rounded-lg text-xs font-medium border border-[var(--color-border)] text-[var(--color-text)]"
          >
            {t('settings.voltageNoticeRetry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-5 border-amber-500/30 bg-amber-500/5">
      <h3 className="text-sm font-semibold flex items-center gap-2 mb-1 text-amber-500">
        <Zap className="w-4 h-4" />
        {t('settings.voltageNoticeTitle')}
      </h3>
      <p className="text-xs text-[var(--color-text-muted)] mb-4">{t('settings.voltageNoticeDesc')}</p>

      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4 space-y-4">
        <label className="flex items-center gap-2.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={e => save({ ...cfg, enabled: e.target.checked })}
            disabled={saving}
            className="w-4 h-4 accent-[var(--color-primary)]"
          />
          <span className="text-[var(--color-text)] font-medium">{t('settings.voltageNoticeEnabled')}</span>
        </label>

        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1.5">
            {t('settings.voltageNoticeEs')}
          </label>
          <textarea
            value={cfg.es}
            onChange={e => setCfg({ ...cfg, es: e.target.value })}
            rows={5}
            maxLength={1500}
            className="w-full text-xs rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] p-2.5"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1.5">
            {t('settings.voltageNoticePt')}
          </label>
          <textarea
            value={cfg.pt}
            onChange={e => setCfg({ ...cfg, pt: e.target.value })}
            rows={5}
            maxLength={1500}
            className="w-full text-xs rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] p-2.5"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => save(cfg)}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--color-primary)] text-white disabled:opacity-60"
          >
            {saving ? t('settings.voltageNoticeSaving') : t('settings.voltageNoticeSave')}
          </button>
          {msg && (
            <span className={`text-xs ${msg.kind === 'ok' ? 'text-green-600' : 'text-red-500'}`}>
              {msg.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface RuleConfig {
  id: string;
  trigger: string;
  options: { key: string; label: string }[];
  selected: number; // index of default selected option
}

interface RuleGroup {
  module: string;
  moduleKey: string;
  icon: typeof Package;
  color: string;
  borderColor: string;
  rules: RuleConfig[];
}

export function BusinessRulesTab({ t }: { t: (k: string) => string }) {
  const ruleGroups: RuleGroup[] = [
    {
      module: t('settings.rulesSales'),
      moduleKey: 'sales',
      icon: ShoppingBag,
      color: 'text-orange-500',
      borderColor: 'border-orange-500/30 bg-orange-500/5',
      rules: [
        {
          id: 'order_cancelled',
          trigger: t('settings.ruleOrderCancelled'),
          options: [
            { key: 'auto_cancel', label: t('settings.ruleOrderCancelledOpt1') },
            { key: 'notify', label: t('settings.ruleOrderCancelledOpt2') },
            { key: 'nothing', label: t('settings.ruleOrderCancelledOpt3') },
          ],
          selected: 0,
        },
        {
          id: 'claim_opened',
          trigger: t('settings.ruleClaimOpened'),
          options: [
            { key: 'pause_notify', label: t('settings.ruleClaimOpenedOpt1') },
            { key: 'notify', label: t('settings.ruleClaimOpenedOpt2') },
            { key: 'nothing', label: t('settings.ruleClaimOpenedOpt3') },
          ],
          selected: 0,
        },
        {
          id: 'unpaid_48h',
          trigger: t('settings.ruleUnpaid48h'),
          options: [
            { key: 'notify', label: t('settings.ruleUnpaid48hOpt1') },
            { key: 'cancel', label: t('settings.ruleUnpaid48hOpt2') },
            { key: 'nothing', label: t('settings.ruleUnpaid48hOpt3') },
          ],
          selected: 0,
        },
      ],
    },
    {
      module: t('settings.rulesPurchases'),
      moduleKey: 'purchases',
      icon: Package,
      color: 'text-purple-500',
      borderColor: 'border-purple-500/30 bg-purple-500/5',
      rules: [
        {
          id: 'price_increase',
          trigger: t('settings.rulePriceIncrease'),
          options: [
            { key: 'pause_notify', label: t('settings.rulePriceIncreaseOpt1') },
            { key: 'buy_anyway', label: t('settings.rulePriceIncreaseOpt2') },
            { key: 'auto_adjust', label: t('settings.rulePriceIncreaseOpt3') },
          ],
          selected: 0,
        },
        {
          id: 'purchase_delivered',
          trigger: t('settings.rulePurchaseDelivered'),
          options: [
            { key: 'auto_stock', label: t('settings.rulePurchaseDeliveredOpt1') },
            { key: 'manual', label: t('settings.rulePurchaseDeliveredOpt2') },
          ],
          selected: 0,
        },
        {
          id: 'stock_zero',
          trigger: t('settings.ruleStockZero'),
          options: [
            { key: 'pause_listing', label: t('settings.ruleStockZeroOpt1') },
            { key: 'auto_po', label: t('settings.ruleStockZeroOpt2') },
            { key: 'notify', label: t('settings.ruleStockZeroOpt3') },
          ],
          selected: 0,
        },
      ],
    },
    {
      module: t('settings.rulesListings'),
      moduleKey: 'listings',
      icon: TrendingUp,
      color: 'text-blue-500',
      borderColor: 'border-blue-500/30 bg-blue-500/5',
      rules: [
        {
          id: 'listing_rejected',
          trigger: t('settings.ruleListingRejected'),
          options: [
            { key: 'ai_fix', label: t('settings.ruleListingRejectedOpt1') },
            { key: 'notify', label: t('settings.ruleListingRejectedOpt2') },
            { key: 'nothing', label: t('settings.ruleListingRejectedOpt3') },
          ],
          selected: 0,
        },
        {
          id: 'margin_low',
          trigger: t('settings.ruleMarginLow'),
          options: [
            { key: 'auto_adjust', label: t('settings.ruleMarginLowOpt1') },
            { key: 'pause', label: t('settings.ruleMarginLowOpt2') },
            { key: 'notify', label: t('settings.ruleMarginLowOpt3') },
          ],
          selected: 0,
        },
        {
          id: 'amazon_oos',
          trigger: t('settings.ruleAmazonOOS'),
          options: [
            { key: 'pause', label: t('settings.ruleAmazonOOSOpt1') },
            { key: 'alt_supplier', label: t('settings.ruleAmazonOOSOpt2') },
            { key: 'notify', label: t('settings.ruleAmazonOOSOpt3') },
          ],
          selected: 0,
        },
      ],
    },
    {
      module: t('settings.rulesWarehouse'),
      moduleKey: 'warehouse',
      icon: Store,
      color: 'text-green-500',
      borderColor: 'border-green-500/30 bg-green-500/5',
      rules: [
        {
          id: 'batch_cancelled',
          trigger: t('settings.ruleBatchCancelled'),
          options: [
            { key: 'alert_block', label: t('settings.ruleBatchCancelledOpt1') },
            { key: 'notify', label: t('settings.ruleBatchCancelledOpt2') },
          ],
          selected: 0,
        },
        {
          id: 'batch_complete',
          trigger: t('settings.ruleBatchComplete'),
          options: [
            { key: 'auto_release', label: t('settings.ruleBatchCompleteOpt1') },
            { key: 'manual', label: t('settings.ruleBatchCompleteOpt2') },
          ],
          selected: 0,
        },
      ],
    },
  ];

  const [selections, setSelections] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    ruleGroups.forEach(g => g.rules.forEach(r => { init[r.id] = r.selected; }));
    return init;
  });

  const [saving, setSaving] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Load from backend on mount
  useEffect(() => {
    api.get<Array<{ rule_key: string; selected_option: number }>>('/rules').then(res => {
      if (res.success && res.data) {
        const fromDb: Record<string, number> = {};
        (res.data as any[]).forEach((r: any) => { fromDb[r.rule_key] = r.selected_option; });
        setSelections(prev => ({ ...prev, ...fromDb }));
      }
    });
  }, []);

  const handleSelect = async (ruleId: string, optIndex: number) => {
    setSelections(prev => ({ ...prev, [ruleId]: optIndex }));
    setSaving(ruleId);
    const res = await api.put(`/rules/${ruleId}`, { selected_option: optIndex });
    setSaving(null);
    if (res.success) {
      setToast(t('settings.rulesSaved'));
      setTimeout(() => setToast(null), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.rulesTitle')}</h2>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">{t('settings.rulesDesc')}</p>
      </div>

      {toast && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 text-green-600 text-xs font-medium border border-green-500/20">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {toast}
        </div>
      )}



      <div className="space-y-5">
        <VoltageNoticeCard t={t} />

        {ruleGroups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.moduleKey} className={`rounded-lg border p-5 ${group.borderColor}`}>
              <h3 className={`text-sm font-semibold flex items-center gap-2 mb-4 ${group.color}`}>
                <GroupIcon className="w-4 h-4" />
                {group.module}
              </h3>

              <div className="space-y-4">
                {group.rules.map(rule => (
                  <div key={rule.id} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
                    {/* Trigger */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                        {t('settings.rulesWhen')}
                      </span>
                      <span className="text-sm font-medium text-[var(--color-text)]">{rule.trigger}</span>
                    </div>

                    {/* Action options */}
                    <div className="ml-0.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] mb-2 inline-block">
                        {t('settings.rulesThen')}
                      </span>
                      <div className="space-y-1.5 mt-2">
                        {rule.options.map((opt, oi) => {
                          const isSelected = selections[rule.id] === oi;
                          return (
                            <label
                              key={opt.key}
                              onClick={() => handleSelect(rule.id, oi)}
                              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs ${
                                isSelected
                                  ? 'bg-[var(--color-primary-light)] border border-[var(--color-primary)]/30'
                                  : 'hover:bg-[var(--color-surface-hover)] border border-transparent'
                              }`}
                            >
                              {isSelected ? (
                                <CircleDot className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                              ) : (
                                <Circle className="w-4 h-4 text-[var(--color-text-muted)] shrink-0" />
                              )}
                              <span className={isSelected ? 'text-[var(--color-text)] font-medium' : 'text-[var(--color-text-secondary)]'}>
                                {opt.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
